import type React from "react";
import { useCallback, useMemo, useState } from "react";

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Optional icon rendered before the label inside the chip (trigger) and in the dropdown list item. */
  icon?: React.ReactNode;
  /** What the row weighs (a measure at this value), for a share bar; absent when unknown. */
  measure?: number | null;
}

export interface UseMultiSelectSearchOptions<T extends MultiSelectOption> {
  value: string[];
  options: T[];
  onChange: (next: string[]) => void;
}

export interface UseMultiSelectSearch<T extends MultiSelectOption> {
  open: boolean;
  query: string;
  filtered: T[];
  displayLabel: string;
  setOpen: (open: boolean) => void;
  toggleOpen: () => void;
  setQuery: (q: string) => void;
  toggleOption: (optionValue: string) => void;
  close: () => void;
}

export const useMultiSelectSearch = <T extends MultiSelectOption>({
  value,
  options,
  onChange,
}: UseMultiSelectSearchOptions<T>): UseMultiSelectSearch<T> => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const toggleOption = useCallback(
    (optionValue: string) => {
      if (value.includes(optionValue)) {
        onChange(value.filter((v) => v !== optionValue));
      } else {
        onChange([...value, optionValue]);
      }
    },
    [value, onChange],
  );

  const toggleOpen = useCallback(() => setOpen((o) => !o), []);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
  }, []);

  const displayLabel = useMemo(() => {
    if (value.length === 0) return "";
    if (value.length <= 2) return value.join(", ");
    return `${value.slice(0, 2).join(", ")} +${value.length - 2} more`;
  }, [value]);

  return {
    open,
    query,
    filtered,
    displayLabel,
    setOpen,
    toggleOpen,
    setQuery,
    toggleOption,
    close,
  };
};
